import { useEffect, useState } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { AutoRenewalDetail } from './types';

const ENDPOINT = '/services_api/storefront/v1/auto_renewals';

/**
 * What every request on this page does when the answer is "who are you?".
 *
 * The controller turned away anyone who was not signed in before this page existed, so a
 * 401 here means the session lapsed in between. The only honest thing left to draw is the
 * login form. `loginUrl` is the server's and points back at this subscription; it is
 * relative to the site root like every other url handed to a component, so it goes
 * through `buildUrl` before the browser follows it.
 */
function isUnauthorized(response: Response): boolean {
  return response.status === 401 || response.status === 403;
}

/**
 * The subscription this page is about, fetched once when the page mounts.
 *
 * The same bargain the list makes, for the same reasons: nothing behind the login wall is
 * indexed, so there is nothing to gain by rendering it on the server, and props that are
 * the same for every visitor are props that can be cached.
 *
 * Three outcomes rather than the list's two. A 404 is not a failure — it is what the api
 * says about an id that is not this customer's, or was never anyone's, and the page has
 * always had a line for it. headless_jshop wrote that line but could not reach it: its
 * fetcher threw on every non-200, so a mistyped url showed "Received unexpected response
 * code: 404" instead. Separating the two is the point of `notFound`.
 */
export default function useAutoRenewal(id: string, loginUrl: string) {
  const { buildUrl } = useUrlGenerator();
  const [autoRenewal, setAutoRenewal] = useState<AutoRenewalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    // Written as one await chain rather than .then()s so that "handled, stop here" is a
    // `return` rather than a sentinel value. It was null, which is also a thing the body
    // could legitimately parse to — and a 200 carrying `null` would then have been read
    // as "already handled" and left the page spinning forever.
    (async () => {
      try {
        const response = await fetch(buildUrl(`${ENDPOINT}/${encodeURIComponent(id)}.json`), {
          signal: controller.signal,
        });

        if (isUnauthorized(response)) {
          window.location.assign(buildUrl(loginUrl));

          // Nothing after this matters — the browser is leaving. Staying in the loading
          // state is deliberate: "no subscription found" would flash under the
          // navigation, which is not what happened.
          return;
        }

        if (response.status === 404) {
          setNotFound(true);
          setIsLoading(false);

          return;
        }

        if (!response.ok) {
          throw new Error(`Auto renewal answered ${response.status}`);
        }

        const answer = (await response.json()) as AutoRenewalDetail | null;

        if (!answer) {
          setNotFound(true);
          setIsLoading(false);

          return;
        }

        setAutoRenewal(answer);
        setIsLoading(false);
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          return;
        }

        console.error('Failed to load auto renewal', error);
        setHasFailed(true);
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
    // `buildUrl` is derived from the site, which does not change within a page, and the
    // id from the url, which does not either.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    autoRenewal,
    isLoading,
    notFound,
    hasFailed,
    /**
     * Every action answers with the whole updated subscription, so the page redraws from
     * the api's own version of it rather than from a guess about what changed. This is
     * what SWR's `populateCache` did at the source.
     */
    onUpdated: setAutoRenewal,
  };
}

type ActionArgs = {
  id: string;
  loginUrl: string;
  /** The action's path under the subscription — 'disable', 'delay', 'apply_offer'. */
  action: string;
  /** What to say when the request fails; it is shown to the customer verbatim. */
  errorMessage: string;
  onSuccess: (autoRenewal: AutoRenewalDetail) => void;
};

/**
 * One of the three things a customer can do to a subscription.
 *
 * All three are the same request with a different path and body, and all three answer
 * with the updated subscription, so they are one hook rather than three near-copies —
 * headless_jshop had three, differing only in the message and the arguments.
 *
 * The error is deliberately a message rather than the failure: the api answers a bad form
 * with per-field errors that no part of this page can act on, and a customer reading
 * "This value should not be blank" about a field they never saw is worse than being told
 * to try again.
 */
export function useAutoRenewalAction({ id, loginUrl, action, errorMessage, onSuccess }: ActionArgs) {
  const { buildUrl } = useUrlGenerator();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Deliberately not memoized. Every call site passes a fresh inline `onSuccess`, and
  // none of the children are memo'd, so a stable identity would buy nothing — while a
  // useCallback that excluded `onSuccess` from its deps (the only way to get one) would
  // freeze the first render's closure and run later clicks against stale state.
  const run = async (body?: Record<string, unknown>) => {
    setLoading(true);
    setError(undefined);

    try {
      const response = await fetch(buildUrl(`${ENDPOINT}/${encodeURIComponent(id)}/${action}.json`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });

      if (isUnauthorized(response)) {
        window.location.assign(buildUrl(loginUrl));

        // Left loading on purpose: the button stays disabled while the browser leaves,
        // rather than coming back to life for a click that would do nothing.
        return;
      }

      if (!response.ok) {
        throw new Error(`Auto renewal ${action} answered ${response.status}`);
      }

      onSuccess((await response.json()) as AutoRenewalDetail);
      setLoading(false);
    } catch (caught: unknown) {
      console.error(`Failed to ${action} auto renewal`, caught);
      setError(new Error(errorMessage));
      setLoading(false);
    }
  };

  return { run, loading, error, reset: () => setError(undefined) };
}
